import { useCallback, useMemo, useRef } from 'react';

import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import {
  type ActiveFilters,
  isValidStatus,
} from '../components/searchControls';

type TeamTableFilterPath = '/team' | '/settings/agents';

export const useTeamTableFilters = (path: TeamTableFilterPath) => {
  const { params, setParams } = useAppSearchParams(path);

  const page = useMemo(() => {
    const pageParam = Number(params.page);
    return isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
  }, [params.page]);

  const activeFilters: ActiveFilters = useMemo(() => {
    const statusParam = params.status ?? null;
    const teamParam = params.team;
    const agentParam = params.agent;
    const roleParam = params.role;
    const queueParam = params.queue;

    return {
      status: isValidStatus(statusParam) ? statusParam : null,
      team: Array.isArray(teamParam) ? teamParam : teamParam ? [teamParam] : [],
      agent: Array.isArray(agentParam)
        ? agentParam
        : agentParam
          ? [agentParam]
          : [],
      role: Array.isArray(roleParam) ? roleParam : roleParam ? [roleParam] : [],
      queue: Array.isArray(queueParam)
        ? queueParam
        : queueParam
          ? [queueParam]
          : [],
    };
  }, [params.status, params.team, params.agent, params.role, params.queue]);

  const setPage = useCallback(
    (newPage: number) => {
      setParams({ page: newPage.toString() });
    },
    [setParams],
  );

  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;

  const setActiveFilters = useCallback(
    (updater: ActiveFilters | ((prev: ActiveFilters) => ActiveFilters)) => {
      const newFilters =
        typeof updater === 'function'
          ? updater(activeFiltersRef.current)
          : updater;

      setParams({
        page: '1',
        status: newFilters.status ?? undefined,
        team: newFilters.team.length > 0 ? newFilters.team : undefined,
        agent: newFilters.agent.length > 0 ? newFilters.agent : undefined,
        role: newFilters.role.length > 0 ? newFilters.role : undefined,
        queue: newFilters.queue.length > 0 ? newFilters.queue : undefined,
      });
    },
    [setParams],
  );

  const clearFilters = useCallback(() => {
    setParams({
      page: '1',
      status: undefined,
      team: undefined,
      agent: undefined,
      role: undefined,
      queue: undefined,
    });
  }, [setParams]);

  return { page, activeFilters, setPage, setActiveFilters, clearFilters };
};
